// pageRoutes.js — the pages that have been renamed, and what they used to be called.
//
// A page's URL is its identity in two places that outlive the code: a bookmark somebody saved,
// and the `allowed_pages` string stored against every user in permissions_ettifaq. Renaming a
// route therefore does two things nobody asked for — it 404s the bookmark, and it silently
// revokes access, because userCanAccess compares the stored path to the new one and they no
// longer match.
//
// So a rename records its old name here and nothing is rewritten in the warehouse. Access is
// compared on the CANONICAL id, so a grant stored under either spelling is understood, and
// main.js turns each old path into a redirect so an old link still arrives.
//
// This map only ever grows. An entry is not removed once "everyone has re-saved their
// permissions", because there is no way to know that, and the cost of keeping it is one line.

/** Old page id -> the id it has now. */
export const PAGE_ID_ALIASES = {
  '/daily-training-new': '/daily-training-report',
  '/match-gps': '/post-match-report',
  '/drill-calculator': '/training-drill-calculator',
  '/player-load-tracking': '/individual-player-load-tracking-report',
  '/load-budget': '/load-budget-chronic-load-overview',
  '/wellness-monitoring': '/wellness-monitoring-report',
  '/wellness-longitudinal': '/wellness-longitudinal-tracking',
  '/attacking-p3': '/attacking-chance-creation',
  '/team-hydration': '/hydration',
  '/match-availability': '/availability',
  '/player-profile': '/scouting-recruitment',
  '/video-uploader': '/match-video-uploader',
  '/video-uploader-testing': '/match-video-uploader',
};

/** The id a page is known by today, given any id it has ever had. */
export function canonicalPageId(id) {
  return PAGE_ID_ALIASES[id] || id;
}

/* Renames that are only remembered for the GRANTS stored under them, not as redirects, because
   the old address is a live page again. /metric-explorer was the Team Metric Explorer's first
   name; it is now the page that holds all three explorers, so a grant still stored under it means
   the Team view, and the address opens the new page. /nutrition is the same: it was the page now
   shown as the Skinfolds & Bodyweight view, and is now the page holding all three views. */
const GRANT_ONLY_ALIASES = {
  '/metric-explorer': '/team-metric-explorer',
  '/nutrition': '/skinfolds-bodyweight',
};

/** What a STORED grant means today. */
export function canonicalGrantId(id) {
  return GRANT_ONLY_ALIASES[id] || canonicalPageId(id);
}

/* Pages made of views that used to be pages of their own. The views keep their old ids as their
   grants — nothing in permissions_ettifaq is rewritten, and nobody gains a view they could not
   open before — so the page opens for anyone holding ANY of them, and shows each view only to
   those holding its id. The view ids stay on the Permissions page (Sidebar.js PAGE_CONFIG, marked
   `viewOf`) and out of the sidebar; their old addresses redirect into the page, view picked. */
export const PAGE_VIEWS = {
  '/metric-explorer': {
    team: '/team-metric-explorer',
    player: '/player-metric-explorer',
    manager: '/manager-metric-explorer',
  },
  '/team-analysis': {
    style: '/opposition-analysis',
    attacking: '/attacking-chance-creation',
    defending: '/defending-chance-prevention',
  },
  '/day-to-day-summaries': {
    wellness: '/wellness-monitoring-report',
    training: '/daily-training-report',
    match: '/post-match-report',
  },
  '/match-performance': {
    'over-time': '/match-physicals-over-time',
    within: '/match-physicals',
    explorer: '/physical-player-metric-explorer',
    minutes: '/match-minutes',
  },
  '/nutrition': {
    bodyweights: '/bodyweights-hydration',
    hydration: '/hydration',
    skinfolds: '/skinfolds-bodyweight',
  },
  /* A new page on an address once used by a deleted board: a grant still stored as /playground is
     no view here, so it opens nothing. */
  '/playground': {
    tracking: '/tracking-playground',
    'body-pose': '/body-pose',
    ghosted: '/statsbomb-tracking-testing',
  },
  '/movement': {
    runs: '/run-types',
    'final-third': '/final-third-movement',
  },
  '/data-pull-performance': {
    catapult: '/catapult',
    'catapult-raw': '/catapult-raw',
    firstbeat: '/firstbeat',
    'firstbeat-raw': '/firstbeat-raw',
    orreco: '/orreco',
  },
  '/data-pull-football': {
    impect: '/impect',
    skillcorner: '/skillcorner',
    statsbomb: '/statsbomb',
    'statsbomb-ghosted': '/statsbomb-tracking-creator',
    opta: '/opta',
  },
};

/** [oldPath, newPath] for every view that used to be a page. */
export const VIEW_REDIRECTS = Object.entries(PAGE_VIEWS).flatMap(([page, views]) => (
  Object.entries(views).map(([view, id]) => [id, `${page}?view=${view}`])
));

/** [oldPath, newPath] for every rename, for the redirect routes. */
export const PAGE_REDIRECTS = Object.entries(PAGE_ID_ALIASES);
